import type { AllocationInfo, OverflowPoolInfo } from '@/types/api'

export type OverflowDisplayState =
  | { mode: 'normal'; hasCapacity: boolean; pctUsed: number }
  | { mode: 'active'; pctUsed: number }
  | { mode: 'exhausted' }

export function getOverflowDisplayState(
  allocation: AllocationInfo,
  pool: OverflowPoolInfo | undefined
): OverflowDisplayState {
  if (!pool) {
    return { mode: 'normal', hasCapacity: false, pctUsed: 0 }
  }

  const pctUsed = pool.pct_used
  const hasCapacity = pool.has_capacity && !pool.is_locked

  const privateCap = allocation.private_cap ?? allocation.cap_total ?? 0
  const used = allocation.used ?? allocation.cap_used ?? 0

  // `used >= privateCap` is vacuously TRUE on a pool-only allocation (both 0), so every geo
  // whose cap comes from the shared pool used to read as spent-out — and during the 00:00–01:00
  // priority-window lock (`!hasCapacity`) that landed on 'exhausted', i.e. hard-stopped at the
  // start of the day with nothing actually used. Requires a real cap to be at it.
  const atCap = privateCap > 0 && used >= privateCap

  if (allocation.status === 'HARD_STOP' || (atCap && !hasCapacity)) {
    return { mode: 'exhausted' }
  }

  if (allocation.status === 'OVERFLOW_ACTIVE' || atCap) {
    return { mode: 'active', pctUsed }
  }

  return { mode: 'normal', hasCapacity, pctUsed }
}
